import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { Sigma, TableProperties } from "lucide-react";
import { PageHeader } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTran } from "@/hooks";
import DataEntryNavTabs from "../components/DataEntryNavTabs";
import OverviewFilterBar from "../components/overview/OverviewFilterBar";
import OverviewTable from "../components/overview/OverviewTable";
import ConsolidatedTable from "../components/consolidated/ConsolidatedTable";
import { loadDisclosures, loadOrgList } from "../operations/load.ops";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";

const DataEntryOverviewPage = observer(() => {
  const { t } = useTran();
  const [searchParams, setSearchParams] = useSearchParams();
  const consolidated = searchParams.get("view") === "consolidated";

  useEffect(() => {
    loadDisclosures({ initFrameworks: true });
    loadOrgList();
    loadReportingYears();
  }, []);

  const toggleConsolidated = (on) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set("view", "consolidated");
    else next.delete("view");
    setSearchParams(next, { replace: true });
  };

  const Icon = consolidated ? Sigma : TableProperties;
  const title = consolidated
    ? t("Consolidated Data")
    : t("Data Entry Overview");
  const description = consolidated
    ? t("Combined values for every metric across your organizations and years.")
    : t("Flat list of every data entry across your organizations.");

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4">
      <PageHeader
        title={title}
        description={description}
        icon={Icon}
        actions={
          <div className="flex flex-col items-end gap-2">
            <DataEntryNavTabs />
            <div className="flex items-center gap-2">
              <Switch
                id="consolidated-toggle"
                checked={consolidated}
                onCheckedChange={toggleConsolidated}
              />
              <Label
                htmlFor="consolidated-toggle"
                className="cursor-pointer text-xs"
              >
                {t("Consolidated")}
              </Label>
            </div>
          </div>
        }
      />

      <div className="space-y-3">
        <OverviewFilterBar />
        {consolidated ? <ConsolidatedTable /> : <OverviewTable />}
      </div>
    </div>
  );
});

export default DataEntryOverviewPage;
